import { LinkInline, Text } from '@velocity/ui'
import * as R from 'ramda'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useContent, useQuery } from '@ngb-frontend/shared/context'
import {
  ContactType,
  type ContactFormValues,
  type ContactStepPrerequisiteData,
  type ContactStepValues,
  type StepProps,
} from '@ngb-frontend/shared/types'
import { FullScreenLoader } from '@ngb-frontend/shared/ui'
import {
  getContactStepDataFromBooking,
  useDriver,
  parseDriverContact,
  useBookingRequest,
  useVehicle,
} from '@ngb-frontend/shared/utils'

import { ContactNotificationBanner } from '../../ContactNotificationBanner/ContactNotificationBanner'
import { ContactForm } from '../../forms/ContactForm/ContactForm'

const defaultFormValues: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
}

const ContactStep: React.FC<
  StepProps<ContactStepValues, ContactStepPrerequisiteData>
> = ({ onNextStep, onUpdateStep, stepData }) => {
  const c = useContent()
  const query = useQuery()
  const { data: vehicle, isLoading: vehicleLoading } = useVehicle(query)

  const { data: bookingData, isLoading: bookingLoading } = useBookingRequest(
    query?.bookingId,
  )
  const bookingContact = useRef<ContactStepValues>()
  const booking = bookingData?.[0]
  // TODO: investigate why in some cases stepData can be an array
  const hasStepData = stepData && !Array.isArray(stepData)

  const { data: sfDriver, isLoading: driverLoading } = useDriver(
    vehicle?.driverIdentifier || query?.licensePlate,
    query,
  )

  // Apply the correct initial data to step
  useEffect(() => {
    if (hasStepData || bookingLoading || vehicleLoading || driverLoading) return

    if (booking) {
      bookingContact.current = getContactStepDataFromBooking(booking)

      onUpdateStep(
        bookingContact.current.contactType === ContactType.Other
          ? bookingContact.current
          : { contactType: ContactType.Driver, contact: defaultFormValues },
      )
      return
    }

    onUpdateStep({
      contactType: ContactType.Driver,
      contact: defaultFormValues,
    })
  }, [
    booking,
    bookingLoading,
    vehicleLoading,
    driverLoading,
    hasStepData,
    onUpdateStep,
  ])

  const disableDriver = sfDriver && (!sfDriver.email || !sfDriver?.mobile_phone)
  const driverFormValues: ContactFormValues | undefined = useMemo(
    () => sfDriver && parseDriverContact(sfDriver),
    [sfDriver],
  )

  const handleSubmit = React.useCallback(
    (values: ContactFormValues, contactType: ContactType) => {
      onUpdateStep({
        contactType,
        contact: values,
      })
      onNextStep()
    },
    [onUpdateStep, onNextStep],
  )

  const onContactChange = useCallback(
    (contactType: ContactType) => {
      onUpdateStep({
        contact:
          bookingContact.current?.contactType === ContactType.Driver
            ? defaultFormValues
            : bookingContact.current?.contact || defaultFormValues,
        contactType,
      })
    },
    [onUpdateStep],
  )

  const privacyLine = useMemo(() => {
    const path = 'steps.shared.contactPrivacy'
    const text = c(path)
    if (!text || text === path) return null

    const privacyLinkText = c('steps.shared.privacyStatement')
    return c.rich(path, {
      variables: {
        link: (chunks) => (
          <LinkInline href={Array.isArray(chunks) ? chunks.join() : chunks}>
            {privacyLinkText}
          </LinkInline>
        ),
      },
    })
  }, [c])

  // Wait for the vehicle and driver requests to settle before rendering the
  // view: the driver radio card only exists while sfDriver is available, and
  // rendering mid-fetch (e.g. while the driver SWR key switches from the
  // country fallback to vehicle.driverIdentifier) makes the card pop in and
  // out of the DOM, breaking user interactions that are already in flight.
  if (!hasStepData || vehicleLoading || driverLoading) {
    return <FullScreenLoader />
  }

  return (
    <>
      <ContactNotificationBanner />
      <ContactForm
        groupLabel={c('steps.contact.title')}
        onContactChange={onContactChange}
        driverFormValues={driverFormValues}
        formValues={stepData.contact}
        contactType={stepData.contactType}
        onSubmit={handleSubmit}
        disableDriver={disableDriver}
      />
      {privacyLine && (
        <Text
          variant="100"
          margin={{ top: '05' }}
          data-e2e-component="contact-privacy"
        >
          {privacyLine}
        </Text>
      )}
    </>
  )
}

const hasContactDetails = (x: unknown): x is ContactFormValues => {
  return R.allPass([
    R.pipe(R.prop('firstName'), R.is(String)),
    R.pipe(R.prop('lastName'), R.is(String)),
    R.pipe(R.prop('email'), R.is(String)),
    R.pipe(R.prop('phone'), R.is(String)),
  ])(x)
}

export const hasContactFormValues = (x: unknown): x is ContactStepValues =>
  R.allPass([
    R.pipe(R.prop('contact'), hasContactDetails),
    R.pipe(R.prop('contactType'), R.is(String)),
  ])(x)

export default ContactStep
